import React, { useEffect, useMemo, useState } from "react";
import "../StockInward.css";
import { getInwardEntries } from "../storage";
import { formatDisplayDate, toComparableYmd } from "../utils/dateFormat";
import { getSettlementSummary, isPaymentSettlement } from "../utils/settlement";

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, "All"];

function formatMoney(value) {
  return (Number(value) || 0).toFixed(2);
}

function makeDefaultFilters() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return {
    fromDate: `${year}-${month}-01`,
    toDate: `${year}-${month}-${day}`,
    supplier: "",
    search: "",
  };
}

function isMaterialPurchaseEntry(entry) {
  return String(entry?.category || "General").trim() === "General";
}

export default function PurchaseReports() {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState(() => makeDefaultFilters());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getInwardEntries();
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      alert("Failed to load material purchase report data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purchaseEntries = useMemo(
    () => (Array.isArray(entries) ? entries : []).filter((entry) => isMaterialPurchaseEntry(entry)),
    [entries]
  );

  const supplierOptions = useMemo(() => {
    return Array.from(
      new Set(
        purchaseEntries
          .map((entry) => String(entry?.supplierName || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [purchaseEntries]);

  useEffect(() => {
    if (!filters.supplier) return;
    if (!supplierOptions.includes(filters.supplier)) {
      setFilters((prev) => ({ ...prev, supplier: "" }));
    }
  }, [supplierOptions, filters.supplier]);

  const reportRows = useMemo(() => {
    const map = new Map();

    for (const entry of purchaseEntries) {
      const supplierName = String(entry?.supplierName || "").trim() || "Unknown Supplier";
      const purchaseDate = toComparableYmd(entry?.createdAt);
      const settlementSummary = getSettlementSummary(Number(entry?.total) || 0, entry?.payments);

      if (purchaseDate) {
        const key = `${supplierName}__${purchaseDate}`;
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            supplierName,
            date: purchaseDate,
            purchaseAmount: 0,
            paidAmount: 0,
            purchaseEntries: 0,
            paymentEntries: 0,
          });
        }
        const row = map.get(key);
        row.purchaseAmount += settlementSummary.totalAmount;
        row.purchaseEntries += 1;
      }

      for (const payment of Array.isArray(entry?.payments) ? entry.payments : []) {
        if (!isPaymentSettlement(payment)) continue;
        const paymentDate = toComparableYmd(payment?.date);
        const paidAmount = Number(payment?.amount) || 0;
        if (!paymentDate || paidAmount <= 0) continue;
        const key = `${supplierName}__${paymentDate}`;
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            supplierName,
            date: paymentDate,
            purchaseAmount: 0,
            paidAmount: 0,
            purchaseEntries: 0,
            paymentEntries: 0,
          });
        }
        const row = map.get(key);
        row.paidAmount += paidAmount;
        row.paymentEntries += 1;
      }
    }

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        purchaseAmount: +Number(row.purchaseAmount || 0).toFixed(2),
        paidAmount: +Number(row.paidAmount || 0).toFixed(2),
        balanceImpact: +Number((Number(row.purchaseAmount || 0) - Number(row.paidAmount || 0)) || 0).toFixed(2),
      }))
      .sort((a, b) => {
        const dateDiff = String(b.date || "").localeCompare(String(a.date || ""));
        if (dateDiff !== 0) return dateDiff;
        return String(a.supplierName || "").localeCompare(String(b.supplierName || ""), undefined, {
          sensitivity: "base",
        });
      });
  }, [purchaseEntries]);

  const filteredRows = useMemo(() => {
    const fromDate = toComparableYmd(filters.fromDate);
    const toDate = toComparableYmd(filters.toDate);
    const supplierFilter = String(filters.supplier || "").trim();
    const search = String(filters.search || "").trim().toLowerCase();

    return reportRows.filter((row) => {
      const rowDate = toComparableYmd(row?.date);
      if (fromDate && (!rowDate || rowDate < fromDate)) return false;
      if (toDate && (!rowDate || rowDate > toDate)) return false;
      if (supplierFilter && String(row?.supplierName || "") !== supplierFilter) return false;
      if (
        search &&
        !`${String(row?.supplierName || "")} ${String(row?.date || "")}`
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }
      return true;
    });
  }, [reportRows, filters]);

  const totals = useMemo(() => {
    const suppliers = new Set();
    let purchaseAmount = 0;
    let paidAmount = 0;

    for (const row of filteredRows) {
      if (row.supplierName) {
        suppliers.add(row.supplierName);
      }
      purchaseAmount += Number(row.purchaseAmount) || 0;
      paidAmount += Number(row.paidAmount) || 0;
    }

    return {
      supplierCount: suppliers.size,
      rowCount: filteredRows.length,
      purchaseAmount: +purchaseAmount.toFixed(2),
      paidAmount: +paidAmount.toFixed(2),
      balanceImpact: +(purchaseAmount - paidAmount).toFixed(2),
    };
  }, [filteredRows]);

  const effectivePageSize = pageSize === "All" ? Math.max(filteredRows.length, 1) : Number(pageSize) || PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / effectivePageSize));

  useEffect(() => {
    setPage(1);
  }, [filters.fromDate, filters.toDate, filters.supplier, filters.search, pageSize]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    if (pageSize === "All") {
      return filteredRows;
    }
    const start = (page - 1) * effectivePageSize;
    return filteredRows.slice(start, start + effectivePageSize);
  }, [effectivePageSize, filteredRows, page, pageSize]);

  const pageStart = filteredRows.length === 0 ? 0 : (page - 1) * effectivePageSize + 1;
  const pageEnd = filteredRows.length === 0 ? 0 : Math.min(page * effectivePageSize, filteredRows.length);

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header" style={{ gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 className="card-title">Material Purchase Report</h2>
            <div style={{ fontSize: "0.9rem", color: "#64748b", fontWeight: 600, marginTop: 4 }}>
              Supplier date-wise purchase and payment report
            </div>
          </div>
          <span className="entry-count">Report 1</span>
        </div>
        <div className="inward-form">
          <div className="form-grid">
            <div className="field fl-field">
              <input
                id="purchase-report-from"
                className="fl-input input-field"
                type="date"
                value={filters.fromDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="purchase-report-from" className="fl-label">
                From Date
              </label>
            </div>
            <div className="field fl-field">
              <input
                id="purchase-report-to"
                className="fl-input input-field"
                type="date"
                value={filters.toDate}
                onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="purchase-report-to" className="fl-label">
                To Date
              </label>
            </div>
            <div className="field fl-field">
              <select
                id="purchase-report-supplier"
                className="fl-input input-field"
                value={filters.supplier}
                onChange={(event) => setFilters((prev) => ({ ...prev, supplier: event.target.value }))}
                style={{ paddingTop: "14px" }}
              >
                <option value="">All Suppliers</option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier} value={supplier}>
                    {supplier}
                  </option>
                ))}
              </select>
              <label htmlFor="purchase-report-supplier" className="fl-label">
                Supplier Name
              </label>
            </div>
            <div className="field fl-field">
              <input
                id="purchase-report-search"
                className="fl-input input-field"
                type="text"
                value={filters.search}
                onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                placeholder=" "
              />
              <label htmlFor="purchase-report-search" className="fl-label">
                Search
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setFilters(makeDefaultFilters())}>
              Reset Filters
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Supplier Purchase / Payment</h2>
          <span className="entry-count">{filteredRows.length} rows</span>
        </div>

        <div style={{ padding: "0 1.5rem 1rem" }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div
              style={{
                minWidth: 140,
                borderRadius: 14,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 700 }}>Suppliers</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#0f172a" }}>{totals.supplierCount}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#1d4ed8", fontWeight: 700 }}>Total Purchased</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#1e3a8a" }}>{formatMoney(totals.purchaseAmount)}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #bbf7d0",
                background: "#f0fdf4",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#15803d", fontWeight: 700 }}>Total Paid</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#166534" }}>{formatMoney(totals.paidAmount)}</div>
            </div>
            <div
              style={{
                minWidth: 180,
                borderRadius: 14,
                border: "1px solid #fde68a",
                background: "#fffbeb",
                padding: "0.9rem 1rem",
              }}
            >
              <div style={{ fontSize: "0.78rem", color: "#b45309", fontWeight: 700 }}>Balance Impact</div>
              <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "#92400e" }}>{formatMoney(totals.balanceImpact)}</div>
            </div>
          </div>
        </div>

        <div className="entries-table-container">
          <table className="entries-table" style={{ minWidth: 920 }}>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Date</th>
                <th style={{ width: 240 }}>Supplier Name</th>
                <th className="text-right" style={{ width: 140 }}>Purchase Entries</th>
                <th className="text-right" style={{ width: 140 }}>Payment Entries</th>
                <th className="text-right" style={{ width: 160 }}>Purchased Amount</th>
                <th className="text-right" style={{ width: 160 }}>Paid Amount</th>
                <th className="text-right" style={{ width: 140 }}>Net</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-state" style={{ padding: 16 }}>
                    No material purchase report data found for the selected filters.
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDisplayDate(row.date)}</td>
                    <td>{row.supplierName}</td>
                    <td className="text-right">{row.purchaseEntries}</td>
                    <td className="text-right">{row.paymentEntries}</td>
                    <td className="text-right" style={{ fontWeight: 700, color: "#1e3a8a" }}>
                      {formatMoney(row.purchaseAmount)}
                    </td>
                    <td className="text-right" style={{ fontWeight: 700, color: "#166534" }}>
                      {formatMoney(row.paidAmount)}
                    </td>
                    <td
                      className="text-right"
                      style={{
                        fontWeight: 700,
                        color: Number(row.balanceImpact || 0) >= 0 ? "#92400e" : "#166534",
                      }}
                    >
                      {formatMoney(row.balanceImpact)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > 0 ? (
          <div
            style={{
              padding: "0 1.5rem 1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: "0.82rem", color: "#475569" }}>
              {`Showing ${pageStart}-${pageEnd} of ${filteredRows.length} rows`}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.82rem", color: "#475569" }}>
                <span>Rows</span>
                <select
                  className="input-field compact"
                  value={pageSize}
                  onChange={(event) => {
                    const value = event.target.value;
                    setPageSize(value === "All" ? "All" : Number(value) || PAGE_SIZE);
                  }}
                  style={{ width: 90 }}
                >
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || pageSize === "All"}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Prev
              </button>
              <span className="entry-count">{`Page ${page} / ${totalPages}`}</span>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages || pageSize === "All"}
                style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
